import type { AdIntelligenceRecordsSearchAdvertiserSpendResponse } from "@similarweb/pro-api-client";

import type { DeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { USD_CURRENCY } from "./searchAdsSpendMapper";
import type { ISearchAdsSpend } from "./types";

interface IWidgetSeriesPoint {
    Key: string;
    Value: number | null;
}

export interface IAdSpendWidgetData {
    /** Shaped for ChartWidget.getChartSeries: domain → web source → dated values. */
    series: Record<string, Record<string, IWidgetSeriesPoint[]>>;
    /** Rate of the selected currency per month, keyed `YYYY-MM-DD`, for the tooltip. */
    rates: Record<string, number>;
    monthsWithoutRates: string[];
}

const toMonthKey = (date: string): string => date.slice(0, 10);

const toRatesByMonth = (
    exchangeRates: AdIntelligenceRecordsSearchAdvertiserSpendResponse["exchangeRates"] | undefined,
    currency: string,
): Record<string, number> => {
    if (currency === USD_CURRENCY) {
        return {};
    }

    return Object.entries(exchangeRates ?? {}).reduce<Record<string, number>>((rates, [date, monthlyRates]) => {
        const rate = monthlyRates?.unitsPerUsd?.[currency];
        if (rate !== undefined) {
            rates[toMonthKey(date)] = rate;
        }
        return rates;
    }, {});
};

/** Adapts the mapped spend to the shape the dashboard chart widgets consume. */
export const toWidgetSeries = (
    spend: ISearchAdsSpend,
    {
        webSource,
        currency,
        exchangeRates,
    }: {
        webSource: DeviceType;
        currency: string;
        exchangeRates: AdIntelligenceRecordsSearchAdvertiserSpendResponse["exchangeRates"] | undefined;
    },
): IAdSpendWidgetData => ({
    series: Object.fromEntries(
        spend.series.map((domainSeries) => [
            domainSeries.domain,
            {
                [webSource]: domainSeries.points.map(({ date, value }) => ({ Key: date, Value: value })),
            },
        ]),
    ),
    rates: toRatesByMonth(exchangeRates, currency),
    monthsWithoutRates: spend.monthsWithoutRates,
});
